import React, { useState } from "react";
import { Play, X } from "lucide-react";
import { HERO, LINKS } from "../mock";
import Reveal from "./Reveal";

const Hero = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section id="top" className="relative grain overflow-hidden pt-[68px]">
      {/* Ambient glow */}
      <div className="absolute inset-x-0 top-0 h-[520px] amber-glow pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-16 sm:pt-20 pb-20">
        {/* Badge */}
        <Reveal className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#d69b4e]/30 bg-[#d69b4e]/[0.07] px-4 py-1.5 text-[0.78rem] font-medium text-[#e8b876]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d69b4e] animate-pulse" />
            {HERO.badge}
          </span>
        </Reveal>

        {/* Title */}
        <Reveal delay={80} className="text-center mt-7">
          <h1 className="font-display font-800 text-white leading-[1.02] max-w-4xl mx-auto tracking-tight">
            <span className="block text-[2.1rem] sm:text-[3.1rem] lg:text-[3.9rem]">
              {HERO.titleTop}
            </span>
            <span className="block gold-text text-[1.5rem] sm:text-[2.1rem] lg:text-[2.5rem] mt-3">
              {HERO.titleBottom}
            </span>
          </h1>
        </Reveal>

        {/* Subtitle */}
        <Reveal delay={160} className="max-w-2xl mx-auto text-center mt-6">
          <p className="text-[#a49e92] text-[1.02rem] sm:text-[1.1rem] leading-relaxed">
            {HERO.subtitle}
          </p>
        </Reveal>

        {/* CTAs */}
        <Reveal delay={220} className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mt-9">
          <a
            href={LINKS.requestInfo}
            className="inline-flex items-center gap-2 rounded-full bg-white text-[#0a0908] font-600 px-6 py-3.5 text-[0.95rem] hover:bg-[#f0ece3] hover:-translate-y-0.5 transition-all duration-200 shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)] w-full sm:w-auto justify-center"
          >
            Explore the specifics <span aria-hidden>→</span>
          </a>
          <a
            href={LINKS.discord}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] text-white font-500 px-6 py-3.5 text-[0.95rem] hover:bg-white/[0.08] hover:border-white/25 transition-all duration-200 w-full sm:w-auto justify-center"
          >
            Join the Discord <span aria-hidden>→</span>
          </a>
        </Reveal>

        {/* Video */}
        <Reveal delay={300} className="mt-14">
          <div className="relative mx-auto max-w-[560px]">
            <div className="relative aspect-[9/11] sm:aspect-[4/5] rounded-[26px] overflow-hidden border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] bg-[#141210]">
              {playing ? (
                <>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${HERO.youtubeId}?autoplay=1&rel=0`}
                    title="Overview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <button
                    onClick={() => setPlaying(false)}
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition"
                    aria-label="Close video"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <img
                    src={HERO.videoThumb}
                    alt="Overview thumbnail"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                  <button
                    onClick={() => setPlaying(true)}
                    className="group absolute inset-0 flex items-center justify-center"
                    aria-label="Play overview video"
                  >
                    <span className="relative flex items-center justify-center w-[76px] h-[76px] rounded-full bg-white/90 backdrop-blur transition-transform duration-300 group-hover:scale-110 shadow-[0_10px_40px_-8px_rgba(0,0,0,0.6)]">
                      <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                      <Play className="w-7 h-7 text-[#0a0908] ml-1 relative z-10" fill="#0a0908" />
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </Reveal>

        {/* Tags */}
        <Reveal delay={360} className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-12">
          {HERO.tags.map((tag, i) => (
            <React.Fragment key={tag}>
              {i > 0 && <span className="hidden sm:block w-1 h-1 rounded-full bg-[#d69b4e]/50" />}
              <span className="text-[0.85rem] sm:text-[0.92rem] text-[#8a8478] font-medium tracking-wide">
                {tag}
              </span>
            </React.Fragment>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

export default Hero;
